import {
  PaginatedVehicles,
  VehicleDetails,
  VehicleSearchParams,
  CheckAvailabilityRequest,
  CheckAvailabilityResponse,
   ApiAvailabilityRange,
  VehiclePackage,
  Vehicle,
  ApiVehicleListItem,
  ApiVehicleDetail,
  ApiVehiclePackage,
} from "@/types/transport/public-transport.types";

const CDN_BASE =
  process.env.NEXT_PUBLIC_SPACES_CDN_URL ??
  "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com";

// Maps the API's raw field names onto the shape the frontend actually uses.
function mapVehicle(raw: ApiVehicleListItem): Vehicle {
  return {
    vehicleUid: raw.uid,
    name:
      raw.customModelName?.trim() ||
      `${raw.vehicleMakeName} ${raw.vehicleModelName}`,
    vehicleCategory: raw.vehicleCategoryName,
    vehicleMake: raw.vehicleMakeName,
    vehicleModel: raw.vehicleModelName,
    year: raw.manufactureYear,
    fuelType: raw.fuelTypeName,
    transmissionType: raw.transmissionTypeName,
    driverOption: raw.driverOptionName,
    seatingCapacity: raw.seatingCapacity,
    luggageCapacity: raw.luggageCapacity,
    hasAirConditioning: raw.hasAirConditioning,
    pricePerDayLkr: raw.startingPriceLkr,
    coverImageUrl: raw.primaryPhotoObjectKey
      ? `${CDN_BASE}/${raw.primaryPhotoObjectKey}`
      : null,
  };
}

function mapVehicleDetail(raw: ApiVehicleDetail): VehicleDetails {
  const sortedPhotos = [...(raw.photos ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
  const primary =
    sortedPhotos.find((p) => p.isPrimary) ?? sortedPhotos[0] ?? null;

   const dp = raw.dailyPricing?.[0] ?? null;
  const dailyPricing = dp
    ? {
        uid: dp.uid,
        pricePerDayWithDriverLkr: dp.pricePerDayWithDriverLkr,
        pricePerDaySelfDriveLkr: dp.pricePerDaySelfDriveLkr,
        includedKmPerDay: dp.includedKmPerDay,
        extraKmRateLkr: dp.extraKmRateLkr,
        waitingChargePerHourLkr: dp.waitingChargePerHourLkr,
        nightChargeLkr: dp.nightChargeLkr,
        driverAccommodationChargeLkr: dp.driverAccommodationChargeLkr,
        securityDepositLkr: dp.securityDepositLkr,
        minimumRentalDays: dp.minimumRentalDays,
        maximumRentalDays: dp.maximumRentalDays,
        notes: dp.notes,
      }
    : null;

  // Prefer self-drive rate as the headline price, fall back to with-driver.
  const headlinePrice =
    dailyPricing?.pricePerDaySelfDriveLkr ??
    dailyPricing?.pricePerDayWithDriverLkr ??
    raw.startingPriceLkr ??
    null;

  return {
    vehicleUid: raw.uid,
    name:
      raw.customModelName?.trim() ||
      `${raw.vehicleMakeName} ${raw.vehicleModelName}`,
    vehicleCategory: raw.vehicleCategoryName,
    vehicleMake: raw.vehicleMakeName,
    vehicleModel: raw.vehicleModelName,
    year: raw.manufactureYear,
    registrationYear: raw.registrationYear,
    fuelType: raw.fuelTypeName,
    transmissionType: raw.transmissionTypeName,
    driverOption: raw.driverOptionName,
    seatingCapacity: raw.seatingCapacity,
    luggageCapacity: raw.luggageCapacity,
    hasAirConditioning: raw.hasAirConditioning,
    currentOdometerKm: raw.currentOdometerKm,
    pricePerDayLkr: headlinePrice,
    coverImageUrl: primary ? `${CDN_BASE}/${primary.objectKey}` : null,
    description: raw.description ?? "",
    images: sortedPhotos.map((p, i) => ({
      imageUrl: `${CDN_BASE}/${p.objectKey}`,
      sortOrder: p.sortOrder ?? i,
    })),
    features: (raw.features ?? []).map((f) => f.featureName),
    dailyPricing,
       airportPricing: (raw.airportPricing ?? []).map((a) => ({
      uid: a.uid,
      airportName: a.airportName,
      destinationName: a.destinationName,
      distanceKm: a.distanceKm,
      oneWayPriceLkr: a.oneWayPriceLkr,
      returnPriceLkr: a.returnPriceLkr,
      includesHighwayCharges: a.includesHighwayCharges,
      includesParkingCharges: a.includesParkingCharges,
    })),
  };
}
function mapVehiclePackage(raw: ApiVehiclePackage): VehiclePackage {
  return {
    packageUid: raw.uid,
    vehicleUid: raw.vehicleUid,
    title: raw.title,
    description: raw.description,
    packageType: raw.packageType,
    startLocationName: raw.startLocationName,
    endLocationName: raw.endLocationName,
    startLocationId: raw.startLocationId,
    endLocationId: raw.endLocationId,
    durationHours: raw.durationHours,
    durationDays: raw.durationDays,
    includedKm: raw.includedKm,
    extraKmRateLkr: raw.extraKmRateLkr,
    basePriceLkr: raw.basePriceLkr,
    waitingChargePerHourLkr: raw.waitingChargePerHourLkr,
    nightChargeLkr: raw.nightChargeLkr,
    driverAccommodationChargeLkr: raw.driverAccommodationChargeLkr,
    inclusions: raw.inclusions,
    exclusions: raw.exclusions,
  };
}

// NOTE: swap this for your existing apiClient/ApiResult<T> wrapper —
// left as plain fetch since the shared client wasn't in scope here.
const BASE = `${process.env.NEXT_PUBLIC_API_BASE_URL ?? ""}/api/public/transport`;

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? `Request failed (${res.status})`);
  }
  return res.json();
}

function buildQuery(params: Record<string, unknown>) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  });
  return q.toString();
}

export const publicTransportService = {
  async searchVehicles(params: VehicleSearchParams): Promise<PaginatedVehicles> {
    const qs = buildQuery({ ...params, page: params.page ?? 1, pageSize: params.pageSize ?? 12 });
    const res = await fetch(`${BASE}/vehicles/search?${qs}`, { cache: "no-store" });
    const raw = await handle<{
      items: ApiVehicleListItem[];
      totalCount: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(res as unknown as Response);
    return { ...raw, items: raw.items.map(mapVehicle) };
  },

 getVehicleById(vehicleUid: string): Promise<VehicleDetails> {
    return fetch(`${BASE}/vehicles/${vehicleUid}`, { cache: "no-store" })
      .then(handle<ApiVehicleDetail>)
      .then(mapVehicleDetail);
  },

   async checkAvailability(
    vehicleUid: string,
    body: CheckAvailabilityRequest,
    pricePerDayLkr?: number | null,
  ): Promise<CheckAvailabilityResponse> {
    const ranges = await this.getAvailabilityCalendar(
      vehicleUid,
      body.pickupDateTime,
      body.returnDateTime,
    );

    const pickup = new Date(body.pickupDateTime).getTime();
    const ret = new Date(body.returnDateTime).getTime();

    // Anything other than "AVAILABLE" that overlaps the window blocks the booking.
    const conflicting = ranges.filter((r) => {
      if (r.availabilityStatus === "AVAILABLE") return false;
      const start = new Date(r.startDate).getTime();
      const end = new Date(r.endDate).getTime();
      return start < ret && end > pickup;
    });

     const available = conflicting.length === 0;
    const totalDays = Math.max(
      1,
      Math.ceil((ret - pickup) / (1000 * 60 * 60 * 24)),
    );

    return {
      available,
      totalDays,
      totalPriceLkr:
        available && pricePerDayLkr ? pricePerDayLkr * totalDays : undefined,
      unavailableStatus: conflicting[0]?.availabilityStatus,
      unavailableReason: conflicting[0]?.reason,
      conflictingBookings: conflicting.map((r) => ({
        startDate: r.startDate,
        endDate: r.endDate,
      })),
    };
  },

  getAvailabilityCalendar(
    vehicleUid: string,
    fromDate: string,
    toDate: string,
  ): Promise<ApiAvailabilityRange[]> {
    const qs = buildQuery({ fromDate, toDate });
    return fetch(`${BASE}/vehicles/${vehicleUid}/availability?${qs}`, {
      cache: "no-store",
    }).then(handle<ApiAvailabilityRange[]>);
  },

  getVehiclePackages(vehicleUid: string): Promise<VehiclePackage[]> {
    return fetch(`${BASE}/vehicles/${vehicleUid}/packages`, {
      cache: "no-store",
    })
      .then(handle<ApiVehiclePackage[]>)
      .then((raw) => raw.map(mapVehiclePackage));
  },
};